import { useState, useCallback } from 'react';
import {
  useGenerate_Signeds3_Request_For_UploadMutation,
  useGenerate_Signeds3_Request_For_DownloadMutation,
} from '../generated/graphql';
import { uploadToS3 } from '../utils/helpers';
import { MAX_KYC_TOTAL_SIZE } from '../shared/constants';


export function useS3Upload(notify?: (success: boolean, message: string) => void) {
  const [uploadMutation] = useGenerate_Signeds3_Request_For_UploadMutation();
  const [downloadMutation] = useGenerate_Signeds3_Request_For_DownloadMutation();
  const [uploading, setUploading] = useState(false);

  const getSignedUrl = useCallback(async (filename: string): Promise<string> => {
    if (!filename || typeof filename !== 'string') return '';
    
    try {
      const ext = filename.split('.').pop() || '';
      const { data } = await downloadMutation({
        variables: { filename, filetype: ext },
      });
      return data?.generateSignedS3RequestForDownload?.signedRequest || '';
    } catch (error) {
      console.error('Failed to get signed URL:', error);
      return '';
    }
  }, [downloadMutation]);

  const validateFileSize = (file: File, maxSize: number = MAX_KYC_TOTAL_SIZE): boolean => {
    if (file.size > maxSize) {
      const maxSizeMB = (maxSize / (1024 * 1024)).toFixed(1);
      const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1);
      notify?.(false, `File "${file.name}" is ${fileSizeMB}MB. Maximum size is ${maxSizeMB}MB`);
      return false;
    }
    return true;
  };

  const uploadSingle = useCallback(async (file: File, maxSize?: number) => {
    if (!validateFileSize(file, maxSize)) {
      throw new Error('File size exceeds limit');
    }

    setUploading(true);
    try {
      const result = await uploadToS3(file, uploadMutation);
      const previewUrl = await getSignedUrl(result.filename);
      notify?.(true, 'Upload successful');
      return { filename: result.filename, previewUrl };
    } catch (error) {
      notify?.(false, 'Upload failed');
      throw error;
    } finally {
      setUploading(false);
    }
  }, [uploadMutation, getSignedUrl, notify]);

  const uploadMultiple = useCallback(async (files: File[], maxTotalSize?: number) => {
    // Validate individual file sizes
    for (const file of files) {
      if (!validateFileSize(file)) {
        throw new Error('File size exceeds limit');
      }
    }

    // Validate total size for multiple files
    if (maxTotalSize) {
      const totalSize = files.reduce((sum, file) => sum + file.size, 0);
      if (totalSize > maxTotalSize) {
        const maxSizeMB = (maxTotalSize / (1024 * 1024)).toFixed(1);
        const totalSizeMB = (totalSize / (1024 * 1024)).toFixed(1);
        notify?.(false, `Total file size is ${totalSizeMB}MB. Maximum is ${maxSizeMB}MB`);
        throw new Error('Total file size exceeds limit');
      }
    }

    setUploading(true);
    try {
      const uploads = await Promise.all(
        Array.from(files).map(file => uploadToS3(file, uploadMutation))
      );
      
      const results = await Promise.all(
        uploads.map(async (result, i) => ({
          filename: result.filename,
          previewUrl: await getSignedUrl(result.filename),
          type: files[i].type,
        }))
      );
      
      notify?.(true, `${files.length} file(s) uploaded successfully`);
      return results;
    } catch (error) {
      notify?.(false, 'Upload failed');
      throw error;
    } finally {
      setUploading(false);
    }
  }, [uploadMutation, getSignedUrl, notify]);

  return { uploading, uploadSingle, uploadMultiple, getSignedUrl };
}